import { LitElement, html, css } from 'lit';

import { store } from '../lib/store.js';

function imageUrlFor(slide) {
  if (!slide?.image) return '';
  if (typeof slide.image === 'string') return slide.image;
  return slide.image.url || '';
}

export class SiteHeroSlider extends LitElement {
  static properties = {
    slides: { type: Array },
    _current: { state: true },
  };

  static styles = css`
    :host {
      display: block;
      position: relative;
      min-height: clamp(40rem, 58vw, 46rem);
      overflow: hidden;
    }
    .slide {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      opacity: 0;
      transition: opacity 0.8s ease, transform 0.8s ease;
      transform: scale(1.05);
    }
    .slide.active {
      opacity: 1;
      transform: scale(1);
      z-index: 2;
    }
    .slide-bg {
      position: absolute;
      inset: 0;
      background-size: cover;
      background-position: center;
      transform: translateZ(0);
    }
    .slide-bg img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      display: block;
    }
    .slide-bg-1 {
      background: transparent;
    }
    .slide-bg-2 {
      background: transparent;
    }
    .slide-bg-3 {
      background: transparent;
    }
    .slide-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, rgba(248,250,255,0.2), rgba(248,250,255,0.75));
    }
    .slide-content {
      position: relative;
      z-index: 3;
      text-align: left;
      width: min(100%, var(--content-max-width, 1440px));
      max-width: none;
      margin: 0 auto;
      padding: calc(var(--header-height, 4rem) + 3rem) var(--content-padding, 2rem) 4rem;
    }
    .slide-title {
      max-width: 50rem;
      font-size: clamp(3.2rem, 7vw, 6rem);
      font-weight: 900;
      line-height: var(--leading-tight, 1.1);
      margin-bottom: var(--space-6, 1.5rem);
      background: linear-gradient(135deg, var(--color-text-primary, #15164a) 0%, var(--color-accent, #5772dc) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .slide-subtitle {
      font-size: var(--text-xl, 1.25rem);
      color: var(--color-text-secondary, #4b5272);
      margin-bottom: var(--space-10, 2.5rem);
      line-height: var(--leading-relaxed, 1.7);
      max-width: 600px;
      margin-left: 0;
      margin-right: 0;
    }
    .slide-cta {
      display: inline-flex;
      gap: var(--space-4, 1rem);
      flex-wrap: wrap;
      justify-content: flex-start;
    }
    .slide-cta a {
      display: inline-flex;
      align-items: center;
      gap: var(--space-2, 0.5rem);
      padding: var(--space-4, 1rem) var(--space-8, 2rem);
      border-radius: var(--border-radius, 0.75rem);
      font-weight: var(--font-semibold, 600);
      font-size: var(--text-base, 1rem);
      text-decoration: none;
      transition: all var(--transition-base, 0.25s ease);
    }
    .slide-cta .btn-primary {
      background: var(--color-cta, #ff6b35);
      color: white;
    }
    .slide-cta .btn-primary:hover {
      background: var(--color-cta-hover, #ff8555);
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(255, 107, 53, 0.35);
    }
    .slide-cta .btn-secondary {
      background: rgba(255,255,255,0.05);
      color: var(--color-text-primary, #f0f0f5);
      border: 1px solid rgba(255,255,255,0.15);
    }
    .slide-cta .btn-secondary:hover {
      background: rgba(255,255,255,0.1);
      transform: translateY(-2px);
    }
    .dots {
      position: relative;
      margin-top: var(--space-5, 1.25rem);
      display: flex;
      gap: var(--space-3, 0.75rem);
      z-index: 5;
    }
    .dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: rgba(87, 114, 220, 0.18);
      border: 1px solid rgba(87, 114, 220, 0.35);
      cursor: pointer;
      transition: all var(--transition-base, 0.25s ease);
    }
    .dot.active {
      background: var(--color-accent, #00d4ff);
      border-color: var(--color-accent, #5772dc);
      box-shadow: 0 8px 18px rgba(87, 114, 220, 0.2);
      width: 32px;
      border-radius: 6px;
    }
    .dot:hover {
      background: rgba(87, 114, 220, 0.28);
    }
    @media (max-width: 768px) {
      .slide-title {
        font-size: var(--text-4xl, 2.5rem);
      }
      .slide-subtitle {
        font-size: var(--text-base, 1rem);
      }
      .slide-cta a {
        padding: var(--space-3, 0.75rem) var(--space-6, 1.5rem);
      }
      :host {
        min-height: 39rem;
      }
    }
  `;

  constructor() {
    super();
    this.slides = [];
    this._current = 0;
    this._interval = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._startAutoRotate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopAutoRotate();
  }

  _startAutoRotate() {
    this._stopAutoRotate();
    this._interval = setInterval(() => {
      this._current = (this._current + 1) % (this.slides?.length || 1);
      this.requestUpdate();
    }, 6000);
  }

  _stopAutoRotate() {
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = null;
    }
  }

  _goTo(index) {
    this._current = index;
    this.requestUpdate();
    this._startAutoRotate();
  }

  _normalizeLink(link) {
    if (link === '/signin' || link === '/signin/') return '/signup/';
    if (!link) return '/';
    return link.endsWith('/') || link.includes('#') || link.includes('?') ? link : `${link}/`;
  }

  render() {
    if (!this.slides || this.slides.length === 0) {
      return '';
    }

    const slideLabel = store.getSiteSettings()?.interface?.slide_navigation_label || 'Slide {number}';

    return html`
      ${this.slides.map((slide, i) => html`
        <div class="slide ${i === this._current ? 'active' : ''}">
          <div class="slide-bg ${slide.bgClass || 'slide-bg-1'}">
            ${imageUrlFor(slide) ? html`
              <img src=${imageUrlFor(slide)} alt=${slide.imageAlt || ''}>
            ` : ''}
          </div>
          <div class="slide-overlay"></div>
          <div class="slide-content">
            <h1 class="slide-title">${slide.title || ''}</h1>
            <p class="slide-subtitle">${slide.subtitle || ''}</p>
            <div class="slide-cta">
              ${slide.cta ? html`
                <a class="btn-primary" href=${this._normalizeLink(slide.ctaLink)}>${slide.cta}</a>
              ` : ''}
              ${slide.cta2 ? html`
                <a class="btn-secondary" href=${this._normalizeLink(slide.cta2Link)}>${slide.cta2}</a>
              ` : ''}
            </div>
            <div class="dots">
              ${this.slides.map((_, dotIndex) => html`
                <button class="dot ${dotIndex === this._current ? 'active' : ''}"
                        @click=${() => this._goTo(dotIndex)}
                        aria-label=${slideLabel.replace('{number}', String(dotIndex + 1))}>
                </button>
              `)}
            </div>
          </div>
        </div>
      `)}
    `;
  }
}

customElements.define('site-hero-slider', SiteHeroSlider);
